import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import TopNav from '../components/TopNav';
import SEO from '../components/SEO';
import api, { getApiError } from '../lib/axios';
import {
  Globe, ExternalLink, Award, ShieldCheck, Loader2, Bell, Brain, Sparkles,
  AlertTriangle, ChevronRight, TrendingUp, TrendingDown, Minus
} from 'lucide-react';
import type { LookupResultItem, PriceListingResult } from '../types';

interface DomainData extends LookupResultItem {}

interface AiAnalysis {
  quality_score: number;
  recommendation: string;
  fair_price_estimate: number | null;
  analysis: string;
  risks: string[];
  opportunities: string[];
  verdict: string;
}

export default function DomainDetail() {
  const { domainName } = useParams<{ domainName: string }>();
  const [domain, setDomain] = useState<DomainData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aiAnalysis, setAiAnalysis] = useState<AiAnalysis | null>(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    if (!domainName) return;
    setLoading(true);
    api.get(`/api/domains/${domainName}`)
      .then((r: any) => {
        const d = r.data;
        setDomain({
          domain: d.domain, dr: d.dr, da: d.da, tf: d.tf, cf: d.cf,
          ref_domains: d.ref_domains, backlinks: d.backlinks, organic_traffic: d.organic_traffic,
          topics: d.topics, language: d.language, country: d.country, category: d.category,
          dofollow: d.dofollow, spam_score: d.spam_score,
          min_price: d.min_price, max_price: d.max_price, avg_price: d.avg_price,
          seller_count: d.seller_count, prices: d.prices || [],
        });
      })
      .catch((err: any) => setError(getApiError(err, 'Domain not found')))
      .finally(() => setLoading(false));
  }, [domainName]);

  const runAi = async () => {
    if (!domainName) return;
    setAiLoading(true);
    try {
      const res = await api.get(`/api/ai/analyze/${domainName}`);
      setAiAnalysis(res.data);
    } catch { setAiAnalysis(null); }
    finally { setAiLoading(false); }
  };

  const fmt = (n: number | null) => {
    if (n === null || n === undefined) return '—';
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toLocaleString();
  };

  const qualityScore = domain?.dr ? Math.min(99, Math.round(domain.dr * 0.95 + (domain.organic_traffic ? Math.min(5, Math.log10(domain.organic_traffic)) : 0))) : null;
  const qualityColor = (s: number) => s >= 80 ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : s >= 50 ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-red-100 text-red-700 border-red-200';

  if (loading) return <div className="min-h-screen bg-white"><TopNav /><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand-600" /></div></div>;
  if (error || !domain) return <div className="min-h-screen bg-white"><TopNav /><div className="max-w-3xl mx-auto px-4 py-20 text-center"><Globe className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">{error || 'Domain not found'}</p><Link to="/" className="mt-4 inline-block text-brand-600 hover:underline">← Back to search</Link></div></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title={`${domain.domain} Guest Post Price — Compare ${domain.seller_count} Sellers`}
        description={`Compare guest post prices for ${domain.domain} from ${domain.seller_count} sellers. Best price: $${domain.min_price?.toFixed(0) || 'N/A'}. DR ${domain.dr || 'N/A'}.`}
        canonical={`/domain/${domain.domain}`}
        noindex={domain.seller_count < 2}
      />
      <TopNav />

      {/* Breadcrumb */}
      <div className="max-w-6xl mx-auto px-4 py-4">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Link to="/" className="hover:text-brand-600">Search</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-slate-900 font-medium">{domain.domain}</span>
        </div>
      </div>

      {/* Domain Header */}
      <div className="max-w-6xl mx-auto px-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center text-2xl font-bold text-slate-500 uppercase">
              {domain.domain.charAt(0)}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-slate-900">{domain.domain}</h1>
                {domain.category && <span className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">{domain.category}</span>}
                {domain.country && <span className="text-sm text-slate-500">{domain.country}</span>}
              </div>
              {domain.topics && <p className="text-sm text-slate-400 mt-1">{domain.topics}</p>}
            </div>
            {qualityScore && (
              <div className={`px-4 py-3 rounded-xl border text-center ${qualityColor(qualityScore)}`}>
                <p className="text-3xl font-bold">{qualityScore}</p>
                <p className="text-xs font-medium">Quality</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="max-w-6xl mx-auto px-4 pb-16 grid lg:grid-cols-5 gap-6">

        {/* LEFT — 60% — Price Comparison */}
        <div className="lg:col-span-3 space-y-6">
          {/* Seller Table */}
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900">Price Comparison</h2>
              <p className="text-sm text-slate-500">{domain.seller_count} seller{domain.seller_count !== 1 ? 's' : ''} offering guest posts</p>
            </div>

            {domain.prices.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/50">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Seller</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Price</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Rating</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const cheapestPrice = Math.min(...domain.prices.map(p => p.price));
                    return domain.prices.map((p) => {
                      const isCheapest = p.price === cheapestPrice;
                      const isFeatured = !!p.seller_featured;
                      const isHighlight = isFeatured || isCheapest;
                      return (
                    <tr key={p.id} className={`border-b border-slate-50 ${isFeatured ? 'bg-amber-50/40' : isCheapest ? 'bg-emerald-50/30' : 'hover:bg-slate-50'}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-900">{p.seller_name || 'Unknown'}</span>
                          {p.seller_verified && <ShieldCheck className="w-4 h-4 text-emerald-500" />}
                          {isFeatured && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-bold">Recommended</span>}
                          {isCheapest && <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-bold">Best Price</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-lg font-bold ${isCheapest ? 'text-emerald-600' : 'text-slate-900'}`}>${p.price.toFixed(0)}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-slate-100 rounded text-xs capitalize text-slate-600">{p.listing_type.replace('_', ' ')}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500">★★★★★</td>
                      <td className="px-6 py-4">
                        {p.seller_website && (
                          <button
                            onClick={() => {
                              api.post('/api/clicks/track', null, { params: { seller_id: p.seller_id, domain: domain.domain } }).catch(() => {});
                              window.open(p.seller_website!, '_blank');
                            }}
                            className={`px-4 py-2 text-sm font-semibold rounded-lg flex items-center gap-1 transition-colors ${
                              isHighlight ? 'bg-brand-600 hover:bg-brand-700 text-white' : 'border border-slate-200 text-slate-700 hover:bg-slate-50'
                            }`}
                          >
                            View on seller <ExternalLink className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            ) : (
              <div className="py-12 text-center text-slate-400"><Minus className="w-8 h-8 mx-auto mb-2" /><p>No price listings available</p></div>
            )}
          </div>

          {/* AI Analysis */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            {!aiAnalysis && !aiLoading && (
              <button onClick={runAi} className="w-full py-3 bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg shadow-violet-500/20">
                <Brain className="w-5 h-5" /> Get AI Analysis <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">GPT-4o</span>
              </button>
            )}
            {aiLoading && <div className="flex items-center justify-center gap-3 py-6"><Loader2 className="w-5 h-5 animate-spin text-violet-500" /><span className="text-sm text-slate-500">AI is analyzing {domain.domain}...</span></div>}
            {aiAnalysis && (
              <div className="space-y-4">
                <div className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-violet-500" /><h3 className="font-bold text-slate-900">AI Analysis</h3></div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-slate-50 rounded-xl p-3 text-center">
                    <p className={`text-2xl font-bold ${aiAnalysis.quality_score >= 7 ? 'text-emerald-600' : aiAnalysis.quality_score >= 4 ? 'text-amber-600' : 'text-red-600'}`}>{aiAnalysis.quality_score}/10</p>
                    <p className="text-xs text-slate-500">Quality</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3 text-center">
                    <p className={`text-lg font-bold capitalize ${aiAnalysis.recommendation === 'buy' ? 'text-emerald-600' : aiAnalysis.recommendation === 'skip' ? 'text-red-600' : 'text-amber-600'}`}>{aiAnalysis.recommendation}</p>
                    <p className="text-xs text-slate-500">Verdict</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3 text-center">
                    <p className="text-lg font-bold text-slate-900">{aiAnalysis.fair_price_estimate ? `$${aiAnalysis.fair_price_estimate}` : '—'}</p>
                    <p className="text-xs text-slate-500">Fair Price</p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 bg-slate-50 rounded-xl p-3">{aiAnalysis.analysis}</p>
                <div className="grid grid-cols-2 gap-3">
                  {aiAnalysis.risks.length > 0 && <div><p className="text-xs font-bold text-red-600 flex items-center gap-1 mb-1"><AlertTriangle className="w-3.5 h-3.5" />Risks</p>{aiAnalysis.risks.map((r, i) => <p key={i} className="text-xs text-slate-500">• {r}</p>)}</div>}
                  {aiAnalysis.opportunities.length > 0 && <div><p className="text-xs font-bold text-emerald-600 flex items-center gap-1 mb-1"><ShieldCheck className="w-3.5 h-3.5" />Opportunities</p>{aiAnalysis.opportunities.map((o, i) => <p key={i} className="text-xs text-slate-500">• {o}</p>)}</div>}
                </div>
                {aiAnalysis.verdict && <p className="text-sm font-medium text-slate-900 bg-brand-50 rounded-xl p-3 border border-brand-100">💡 {aiAnalysis.verdict}</p>}
              </div>
            )}
          </div>

          {/* Price Alert CTA */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-900">Set Price Alert</h3>
              <p className="text-sm text-slate-500">Get notified when prices drop for {domain.domain}</p>
            </div>
            <Link to="/watchlist" className="px-4 py-2 border border-brand-200 text-brand-700 hover:bg-brand-50 rounded-lg text-sm font-medium flex items-center gap-1.5">
              <Bell className="w-4 h-4" /> Create Alert
            </Link>
          </div>
        </div>

        {/* RIGHT — 40% — Domain Metrics */}
        <div className="lg:col-span-2 space-y-6">
          {/* Metrics Card */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Domain Metrics</h3>
            <div className="space-y-3">
              {[
                { label: 'Domain Rating (DR)', value: domain.dr },
                { label: 'Domain Authority (DA)', value: domain.da },
                { label: 'Trust Flow (TF)', value: domain.tf },
                { label: 'Citation Flow (CF)', value: domain.cf },
                { label: 'Organic Traffic', value: domain.organic_traffic ? fmt(domain.organic_traffic) + '/mo' : null },
                { label: 'Referring Domains', value: domain.ref_domains ? fmt(domain.ref_domains) : null },
                { label: 'Total Backlinks', value: domain.backlinks ? fmt(domain.backlinks) : null },
                { label: 'Language', value: domain.language?.toUpperCase() },
                { label: 'Dofollow', value: domain.dofollow !== null ? (domain.dofollow ? 'Yes' : 'No') : null },
                { label: 'Domain Age', value: '—' },
              ].map(m => (
                <div key={m.label} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                  <span className="text-sm text-slate-500">{m.label}</span>
                  <span className="text-sm font-semibold text-slate-900">{m.value ?? '—'}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Quality Breakdown */}
          {qualityScore && (
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Quality Score Breakdown</h3>
              <div className="flex items-center gap-3 mb-4">
                <div className={`px-3 py-2 rounded-xl text-2xl font-bold ${qualityColor(qualityScore)}`}>{qualityScore}</div>
                <span className="text-sm text-slate-500">{qualityScore >= 80 ? 'Excellent' : qualityScore >= 50 ? 'Good' : 'Low'} quality domain</span>
              </div>
              <div className="space-y-3">
                {[
                  { label: 'Authority', value: Math.min(99, (domain.dr || 0) + 3), max: 100 },
                  { label: 'Traffic Quality', value: domain.organic_traffic ? Math.min(99, Math.round(Math.log10(domain.organic_traffic) * 15)) : 30, max: 100 },
                  { label: 'Link Value', value: Math.min(99, (domain.dr || 0) - 2), max: 100 },
                  { label: 'Spam Risk', value: 100 - (domain.spam_score || 5), max: 100 },
                ].map(b => (
                  <div key={b.label}>
                    <div className="flex justify-between text-xs mb-1"><span className="text-slate-500">{b.label}</span><span className="font-semibold text-slate-700">{b.value}</span></div>
                    <div className="h-1.5 bg-slate-100 rounded-full"><div className={`h-full rounded-full ${b.value >= 80 ? 'bg-emerald-500' : b.value >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${b.value}%` }} /></div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
